import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { useMaterialUIController } from 'context';
import { useSnackbar } from 'notistack';
import useApiRequest from 'utils/Apirequest';

function LocationOperationalHoursModal({ open, onClose, locationId, onSave }) {
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;
  const { enqueueSnackbar } = useSnackbar();
  const apiRequest = useApiRequest();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    enabled: false,
    startTime: '00:00',
    endTime: '23:59',
    alertDelayMinutes: 0,
  });

  useEffect(() => {
    if (open && locationId) {
      fetchLocationConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationId]);

  const fetchLocationConfig = async () => {
    if (!locationId) {
      console.error('LocationOperationalHoursModal: locationId is undefined');
      enqueueSnackbar('Location ID is missing. Please try again.', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching config for locationId:', locationId);
      const { data } = await apiRequest('get', `location-operational-hours/${locationId}`, null, {}, {});
      
      
      if (data?.success && data?.data?.activeOperationalHours) {
        const hours = data.data.activeOperationalHours;
        setFormData({
          enabled: hours.enabled || false,
          startTime: hours.startTime || '00:00',
          endTime: hours.endTime || '23:59',
          alertDelayMinutes: data.data.alertDelayMinutes,
        });
      }
    } catch (error) {
      console.error('Error fetching location config:', error);
      enqueueSnackbar('Failed to load location configuration', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!locationId) {
      console.error('LocationOperationalHoursModal: Cannot submit - locationId is undefined');
      enqueueSnackbar('Location ID is missing. Please close and reopen the modal.', { variant: 'error' });
      return;
    }

    // Validate time format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(formData.startTime) || !timeRegex.test(formData.endTime)) {
      enqueueSnackbar('Please enter valid time in HH:mm format', { variant: 'warning' });
      return;
    }

    if (formData.alertDelayMinutes < 0 || formData.alertDelayMinutes === '') {
      enqueueSnackbar('Alert delay must be a positive number', { variant: 'warning' });
      return;
    }

    try {
      console.log('Submitting config for locationId:', locationId, 'with data:', formData);
      const payload = {
        activeOperationalHours: {
          enabled: formData.enabled,
          startTime: formData.startTime,
          endTime: formData.endTime,
          timezone: 'Asia/Dubai',
        },
        alertDelayMinutes: formData.alertDelayMinutes,
      };

      const { data } = await apiRequest(
        'put',
        `location-operational-hours/${locationId}`,
        payload,
        {},
        {}
      );

      if (data?.success) {
        enqueueSnackbar('Location operational hours updated successfully', { variant: 'success' });
        if (onSave) onSave();
        handleClose();
      } else {
        enqueueSnackbar(data?.message || 'Failed to update operational hours', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error saving location config:', error);
      enqueueSnackbar('Failed to save configuration', { variant: 'error' });
    }
  };

  const handleClose = () => {
    setFormData({
      enabled: false,
      startTime: '00:00',
      endTime: '23:59',
      alertDelayMinutes: 0,
    });
    onClose();
  };
  const handleStartTimeChange = (e) => {
    const startTime = e.target.value;

    setFormData((prev) => ({
      ...prev,
      startTime,
      // reset end time if it becomes invalid
      endTime:
        prev.endTime && prev.endTime < startTime ? "" : prev.endTime,
    }));
  };

  const handleEndTimeChange = (e) => {
    const endTime = e.target.value;

    if (formData.startTime && endTime < formData.startTime) {
      enqueueSnackbar(
        "End time cannot be earlier than start time",
        { variant: "error" }
      );
      return;
    }

    setFormData((prev) => ({
      ...prev,
      endTime,
    }));
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        style={
          darkMode
            ? { backgroundColor: '#202940', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
            : { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
        }
      >
        <MDBox display="flex" alignItems="center">
          <LocationOnIcon sx={{ mr: 1 }} />
          <MDTypography variant="h5" fontWeight="medium" color={darkMode ? 'white' : 'dark'}>
            Location Operational Hours
          </MDTypography>
        </MDBox>
        <IconButton aria-label="close" onClick={handleClose} style={darkMode ? { color: '#ffffff' } : {}}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={darkMode ? { backgroundColor: '#1a2035', color: '#ffffff', padding: '24px' } : { padding: '24px' }}>
        {loading ? (
          <MDBox textAlign="center" py={3}>
            <MDTypography variant="body2">Loading...</MDTypography>
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            {/* Enable/Disable Operational Hours */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: darkMode ? '#202940' : '#f8f9fa',
                  border: `1px solid ${darkMode ? '#344767' : '#e9ecef'}`,
                  boxShadow: 'none',
                }}
              >
                <CardContent>
                  <MDBox
                    sx={{
                      p: 1.5,
                      border: `2px solid ${formData.enabled ? '#2e7d32' : '#d32f2f'}`,
                      borderRadius: '8px',
                      backgroundColor: formData.enabled ? (darkMode ? '#1a2940' : '#e8f5e9') : (darkMode ? '#2d1a1a' : '#ffebee'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.enabled}
                          onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                          color="success"
                        />
                      }
                      label={
                        <MDTypography variant="button" fontWeight="bold" color={formData.enabled ? 'success' : 'error'}>
                          {formData.enabled ? 'OPERATIONAL HOURS ENABLED' : 'OPERATIONAL HOURS DISABLED'}
                        </MDTypography>
                      }
                    />
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>

            {/* Working Hours */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: darkMode ? '#202940' : '#f8f9fa',
                  border: `1px solid ${darkMode ? '#344767' : '#e9ecef'}`,
                  boxShadow: 'none',
                }}
              >
                <CardContent>
                  <MDBox display="flex" alignItems="center" mb={2}>
                    <AccessTimeIcon sx={{ mr: 1, color: darkMode ? '#fff' : '#344767' }} />
                    <MDTypography variant="h6" fontWeight="medium" color={darkMode ? 'white' : 'dark'}>
                      Working Hours
                    </MDTypography>
                  </MDBox>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        type="time"
                        label="Start Time"
                        value={formData.startTime}
                        onChange={handleStartTimeChange}
                        // onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                        disabled={!formData.enabled}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        type="time"
                        label="End Time"
                        value={formData.endTime}
                        onChange={handleEndTimeChange}
                        disabled={!formData.enabled}
                        inputProps={{
                          min: formData.startTime || undefined,
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Alert Delay */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: darkMode ? '#202940' : '#f8f9fa',
                  border: `1px solid ${darkMode ? '#344767' : '#e9ecef'}`,
                  boxShadow: 'none',
                }}
              >
                <CardContent>
                  <MDTypography variant="h6" fontWeight="medium" color={darkMode ? 'white' : 'dark'} mb={2}>
                    Additional Settings
                  </MDTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <MDInput
                        fullWidth
                        type="number"
                        label="Alert Delay (minutes)"
                        value={formData.alertDelayMinutes}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (value === "") {
                            setFormData({ ...formData, alertDelayMinutes: "" });
                            return;
                          }

                          const numericValue = parseInt(value, 10);

                          if (Number.isNaN(numericValue)) return;

                          setFormData({
                            ...formData,
                            alertDelayMinutes: Math.min(Math.max(numericValue, 0), 1440),
                          });
                        }}
                        disabled={!formData.enabled}
                        inputProps={{
                          min: 0,
                          max: 1440,
                        }}
                      />

                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        style={
          darkMode
            ? { backgroundColor: '#1a2035', justifyContent: 'flex-end', padding: '20px 24px', gap: '12px' }
            : { justifyContent: 'flex-end', padding: '20px 24px', gap: '12px' }
        }
      >
        <MDButton variant="outlined" color="secondary" onClick={handleClose} sx={{ minWidth: '100px' }}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color={sidenavColor} onClick={handleSubmit} sx={{ minWidth: '140px' }} disabled={loading}>
          Save Configuration
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default LocationOperationalHoursModal;
